import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  AppShell,
  Box,
  Container,
  Flex,
  Group,
  Loader,
  Stack,
  useMantineTheme,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import Confetti from 'react-confetti';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';

import { ConfirmModal } from '~/domains/common/components/Modal/Confirm/ConfirmModal';
import dt from '~/testing';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { LogoRunYourPool } from '~/domains/common/components/AppShellHeader/components/Header/assets/LogoRunYourPool';
import { BRANDS } from '~/domains/auth/consts';
import { LogoOfficeFootballPool } from '~/domains/common/components/AppShellHeader/components/Header/assets/LogoOfficeFootballPool';
import { LogoSplashText } from '~/domains/common/components/AppShellHeader/components/Header/assets/LogoSplashText';
import { useContestTemplate } from '~/domains/contest/hooks/useContestTemplate';
import useRunOnPageLeave from '~/domains/common/hooks/useRunOnPageLeave';
import { Routes } from '~/domains/common/constants/routes';
import isWebView from '~/utils/isWebView';
import { useNativeBrowserLoader } from '~/domains/common/utils/useNativeBrowserLoader';

import { isAnythingLoading, hasValidationErrors } from '../utils/getLoadingStates';
import { ContestManageContext } from '../context/ContestManageContext';
import useSubmit from '../hooks/useSubmit';
import { useInitialSubmit } from '../hooks/useInitialSubmit';
import { ContestManageModalsContext } from '../components/Modals/ContestManageModalsContext';
import { SectionHeader } from '../components/Header/Header';
import { reportManageContestAbandon, reportManageContestStart } from '../analytics';

import { SectionSubmitScreen } from './Sections/SectionSubmitScreen';
import { useSectionStyles } from './Sections/Sections.style';
import { SectionSwitch } from './SectionSwitch/SectionSwitch';
import { SectionSkeleton } from './Sections/SectionSkeleton';

// 60 (header) + 16px (gap) + 74 (progress bar with header)
const MIN_HEIGHT = 150;
const LOCAL_STORAGE_KEY = 'contest:manage:restart:hasSeenIntroModal';

type SectionsProps = {
  leagueId?: string;
  contestTemplateId?: string;
  contestId?: string;
  brand?: BRANDS;
  eoNeedsRestart?: boolean;
  operation: 'create' | 'edit' | 'restart';
  titleConfig: {
    headerTitle: string;
    showSubtitle: boolean;
    reviewTitle: string;
  };
  submitConfig: {
    submitButtonText: string;
  };
};

export function Sections({
  leagueId,
  contestTemplateId,
  brand = BRANDS.SPLASH,
  contestId,
  eoNeedsRestart,
  operation,
  titleConfig,
  submitConfig,
}: SectionsProps) {
  const { classes, cx } = useSectionStyles();
  const theme = useMantineTheme();
  const isMobile = useIsMobile();
  const { t } = useTranslation('contest');
  const isRedirecting = useNativeBrowserLoader();
  const webView = isWebView();

  const {
    sections = [],
    errors,
    values,
    touched,
    allowedTagNames,
    store,
  } = useContext(ContestManageContext);
  const { openModal, modals } = useContext(ContestManageModalsContext);
  const hasSeenIntroModal = localStorage?.getItem(LOCAL_STORAGE_KEY) === 'true';

  const { data: contestTemplate } = useContestTemplate({
    params: {
      contestTemplateId,
    },
  });

  const shouldOpenIntroModal = useMemo(
    () => !modals.some((modal) => modal.type === 'IntroModal'),
    [modals]
  );

  // @TODO: uncomment this out once the copies for the intro modal are up to date
  /* useEffect(() => {
    if (operation === 'create' || operation === 'edit') return;
    if (!contestTemplate || !allowedTagNames || allowedTagNames.length < 1) return;
    if (!hasSeenIntroModal && shouldOpenIntroModal) {
      openModal({
        type: 'IntroModal',
        isEntertainmentOnly: !allowedTagNames.includes(contestTemplate.contestType),
        templateName: contestTemplate.templateName,
        contestType: contestTemplate.contestType.toLowerCase() as
          | 'player_one_and_done'
          | 'player_tier'
          | 'team_pick_em'
          | 'team_survivor',
        brand: brand as 'RYP' | 'OFP',
      }).then(() => {
        localStorage?.setItem(LOCAL_STORAGE_KEY, 'true');
      });
    }
    // Don't add shouldOpenIntroModal to dependency it will cause inifinite loop ⚠️
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allowedTagNames,
    contestTemplate?.contestType,
    contestTemplate?.templateName,
    hasSeenIntroModal,
    openModal,
    operation,
  ]);
*/
  const isInitialMode = !leagueId && !contestTemplateId && operation === 'create';
  const entertainmentOnly = contestTemplate?.settings?.entertainmentOnly;

  const normalSubmit = useSubmit({
    leagueId,
    contestTemplateId,
    contestId,
    brand,
    eoNeedsRestart,
    operation,
    entertainmentOnly,
  });
  const initialSubmit = useInitialSubmit();

  const {
    isSubmitLoading,
    onSubmit,
    isSubmitSuccess,
    isDiscardChangesModalOpen,
    onDiscardChangesModalClose,
    onDiscardChangesModalConfirm,
    data,
  } = isInitialMode ? initialSubmit : normalSubmit;

  const { onAnonymousSubmit, isAnonymous, hasAnonValue, isAnonURL, submitError } = normalSubmit;
  const isAnonComeback = hasAnonValue && isAnonURL && !isInitialMode && !submitError;

  const isDataLoading = useMemo(() => isAnythingLoading(store), [store]);
  const hasErrors = useMemo(() => hasValidationErrors(errors), [errors]);

  // Track if user has attempted to submit (to show validation errors)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Custom submit handler that tracks attempts
  const handleSubmit = useCallback(() => {
    setHasAttemptedSubmit(true);
    onSubmit();
  }, [onSubmit]);

  // Auto-submit on return from login after anonymous creation
  const hasAutoSubmitted = useRef(false);
  useEffect(() => {
    if (hasAnonValue && isAnonURL && !hasAutoSubmitted.current) {
      hasAutoSubmitted.current = true;
      handleSubmit();
    }
  }, [hasAnonValue, isAnonURL, handleSubmit]);

  // Reset attempt tracking when form becomes valid or when successful
  useEffect(() => {
    if (isSubmitSuccess || (!hasErrors && hasAttemptedSubmit)) {
      setHasAttemptedSubmit(false);
    }
  }, [isSubmitSuccess, hasErrors, hasAttemptedSubmit]);

  const isSubmitSuccessRef = useRef(isSubmitSuccess);
  isSubmitSuccessRef.current = isSubmitSuccess;
  const isAnonymousRef = useRef(isAnonymous);
  isAnonymousRef.current = isAnonymous;

  const hasReportedStartRef = useRef(false);
  useEffect(() => {
    if (hasReportedStartRef.current) return;
    if (operation === 'restart' && !contestTemplate) return;

    reportManageContestStart({
      operation,
      contestTemplateName: contestTemplate?.templateName,
      contestType: contestTemplate?.contestType,
      contestLeague: contestTemplate?.leagueId,
    });

    hasReportedStartRef.current = true;
  }, [contestTemplate, operation]);

  const abandonValuesRef = useRef<Parameters<typeof reportManageContestAbandon>[0] | null>(null);
  abandonValuesRef.current = {
    operation,
    contestTemplateName: contestTemplate?.templateName,
    contestType: contestTemplate?.contestType,
    contestLeague: contestTemplate?.leagueId,
    values,
    errors,
    touched,
  };

  const handleAbandon = useCallback((e: BeforeUnloadEvent | PopStateEvent | Event) => {
    if (isSubmitSuccessRef.current || isAnonymousRef.current) {
      return;
    }

    e.preventDefault();
    e.returnValue = '';

    reportManageContestAbandon(abandonValuesRef.current);
  }, []);

  useRunOnPageLeave(handleAbandon);
  // Separate effect for component unmount
  useEffect(
    () => () => {
      if (isSubmitSuccessRef.current || isAnonymousRef.current) {
        return;
      }

      reportManageContestAbandon(abandonValuesRef.current);
    },
    []
  );

  const form = values?.form;
  const isSelfManagedSelected =
    operation !== 'edit' &&
    form?.entriesSettings?.entryFeeSettings?.settings?.mode === 'self' &&
    (eoNeedsRestart || contestTemplate?.settings?.entertainmentOnly?.url);

  const showAnonymousButton =
    isAnonymous && operation === 'create' && !isInitialMode && !isSelfManagedSelected;

  const submitButtonText = useMemo(() => {
    if (isSubmitLoading || isRedirecting) {
      return <Loader size={18} />;
    }

    if (isSelfManagedSelected) {
      return t('manage.restart.sections.contestDetails');
    }

    return submitConfig.submitButtonText;
  }, [isSubmitLoading, isRedirecting, isSelfManagedSelected, submitConfig.submitButtonText, t]);

  return (
    <AppShell
      header={
        <SectionHeader
          title={titleConfig.headerTitle}
          contestName={form?.contestName}
          closeUrl={() => window.history.back()}
        />
      }
      styles={{ main: { padding: 0 } }}
    >
      <div className={classes.pageWrapper}>
        {isSubmitSuccess ? <Confetti recycle={false} /> : null}
        <Container size="md" pt={theme.other.spacing._40} className={classes.container}>
          <Stack className={classes.header} spacing={theme.other.spacing._8}>
            {titleConfig.showSubtitle && brand ? (
              <Flex w="100%" align="center" justify="center" gap={theme.other.spacing._4}>
                <Box className={classes.indicator} />
                <Text variant="subhead-medium" c={theme.colors.gray2[3]}>
                  {t('manage.restart.subTitle')}
                </Text>
              </Flex>
            ) : null}

            <Flex pos="relative" align="center">
              <div
                className={cx(classes.title, { webView })}
                data-test-id={dt.contest.create.header.title}
              >
                {isSubmitSuccess ? t('manage.restart.title.created') : titleConfig.reviewTitle}
              </div>
              {webView ? (
                <Flex className={cx(classes.closeButton, { webView })} justify="end">
                  <ActionIcon
                    variant="transparent"
                    radius="xl"
                    component={Link}
                    href={Routes.home()}
                  >
                    <IconX color={theme.colors.gray2[9]} />
                  </ActionIcon>
                </Flex>
              ) : null}
            </Flex>
          </Stack>
          <Flex
            justify="space-between"
            /* eslint-disable-line local-rules/use-theme-tokens */ gap={20}
            direction="column"
            mih={`calc(100vh - ${isMobile ? 16 : MIN_HEIGHT}px)`}
            pt="md"
          >
            {(() => {
              if (isSubmitSuccess) return <SectionSubmitScreen contestId={data?.id} />;

              if (isAnonComeback) {
                return (
                  <Flex justify="center" align="center" py="xl">
                    <Loader size={32} />
                  </Flex>
                );
              }

              return (
                <>
                  <div>
                    {sections.length === 0 && <SectionSkeleton currentSectionKey="init" />}
                    {sections.map((sectionKey) => (
                      <SectionSwitch key={sectionKey} sectionKey={sectionKey} />
                    ))}
                  </div>
                  <Group className={classes.actions}>
                    {showAnonymousButton ? (
                      <Button
                        variant="primary-fill"
                        className={classes.button}
                        disabled={isDataLoading || (hasAttemptedSubmit && hasErrors)}
                        onClick={() => {
                          setHasAttemptedSubmit(true);
                          onAnonymousSubmit(values);
                        }}
                        data-test-id={dt.contest.create.action.login}
                      >
                        {t('commissioner:common.signUpToCreate')}
                      </Button>
                    ) : (
                      <Button
                        variant="primary-fill"
                        className={classes.button}
                        disabled={
                          isSubmitLoading ||
                          isRedirecting ||
                          isDataLoading ||
                          (hasAttemptedSubmit && hasErrors)
                        }
                        onClick={handleSubmit}
                        data-test-id={dt.contest.create.action.submit}
                      >
                        {submitButtonText}
                      </Button>
                    )}
                  </Group>
                </>
              );
            })()}
          </Flex>
        </Container>
        <ConfirmModal
          cancelText="Cancel"
          confirmText="Exit"
          description="All changes will be lost"
          isOpen={isDiscardChangesModalOpen}
          onClose={onDiscardChangesModalClose}
          onConfirm={onDiscardChangesModalConfirm}
          title="Are you sure you want to exit?"
        />
      </div>
    </AppShell>
  );
}
